import type { ReportResponse } from './report.types'

const MALFORMED_REPORT_RESPONSE = 'Malformed report response'

const REPORT_PAYLOAD_KEYS = [
  'data',
  'error',
  'total',
  'total_other',
  'total_period',
  'grand_total',
  'page_total',
  'total_rows',
  'pagination',
  'dropdownable_dimensions',
  'other_data',
  // Promoted like any other payload key so the funnel can read them off the root regardless of
  // whether the controller wrapped the answer in `combined` (v3 read `data.combined || data`).
  'processing',
  'retry_after_sec',
] as const

function isBoundary(character: string | undefined): boolean {
  return character === undefined || !/[\w.]/u.test(character)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function hasOwn(record: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key)
}

/** Replace invalid JSON numeric tokens without altering quoted text such as "Infinity Ads". */
export function replaceInvalidReportNumbers(source: string): string {
  let output = ''
  let inString = false
  let escaped = false

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index] as string
    if (inString) {
      output += character
      if (escaped) escaped = false
      else if (character === '\\') escaped = true
      else if (character === '"') inString = false
      continue
    }
    if (character === '"') {
      inString = true
      output += character
      continue
    }

    const token = ['-Infinity', 'Infinity', 'NaN'].find(
      (candidate) =>
        source.startsWith(candidate, index) &&
        isBoundary(source[index - 1]) &&
        isBoundary(source[index + candidate.length]),
    )
    if (token) {
      output += '0'
      index += token.length - 1
    } else {
      output += character
    }
  }
  return output
}

/** A still-computing controller answers `processing: true` with no rows yet (v3 reportStore.ts:198). */
function isProcessing(payload: Record<string, unknown>): boolean {
  return payload.processing === true
}

/** Rows are simply not ready yet, so normalize to an empty page rather than a missing `data`. */
function withRows(payload: Record<string, unknown>): ReportResponse {
  return { ...payload, data: Array.isArray(payload.data) ? payload.data : [] } as ReportResponse
}

function normalizeReportResponse(value: unknown): ReportResponse {
  if (!isRecord(value)) throw new TypeError(MALFORMED_REPORT_RESPONSE)

  if (!hasOwn(value, 'combined')) {
    // Checked BEFORE the rows requirement: a processing answer legitimately has no `data`, and
    // rejecting it here is what made a computing report surface as a decode error.
    if (isProcessing(value)) return withRows(value)
    if (!Array.isArray(value.data)) throw new TypeError(MALFORMED_REPORT_RESPONSE)
    return value as ReportResponse
  }

  if (!isRecord(value.combined)) throw new TypeError(MALFORMED_REPORT_RESPONSE)
  const combined = value.combined
  const processing = isProcessing(combined) || isProcessing(value)
  if (!processing && !Array.isArray(combined.data)) {
    throw new TypeError(MALFORMED_REPORT_RESPONSE)
  }

  const rootRetryAfterSec = value.retry_after_sec
  const normalized: Record<string, unknown> = { ...value }

  // A combined payload is a complete authority, not a sparse overlay. Clear stale root metadata
  // before promoting only the known keys that the combined payload actually owns.
  for (const key of REPORT_PAYLOAD_KEYS) delete normalized[key]
  for (const key of REPORT_PAYLOAD_KEYS) {
    if (hasOwn(combined, key)) normalized[key] = combined[key]
  }

  if (!processing) return normalized as ReportResponse
  // Survive an envelope that states `processing` at the root but does not repeat it inside.
  normalized.processing = true
  if (normalized.retry_after_sec === undefined && rootRetryAfterSec !== undefined) {
    normalized.retry_after_sec = rootRetryAfterSec
  }
  return withRows(normalized)
}

/** Decode the controller's legacy JSON and normalize its optional multi-workspace envelope. */
export function parseReportResponseBody(body: unknown): ReportResponse {
  const parsed: unknown =
    typeof body === 'string' ? JSON.parse(replaceInvalidReportNumbers(body)) : body
  return normalizeReportResponse(parsed)
}
